import { getDashboardKpiCards } from '@/api/dashboardApi'
import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

// ==================== Constants ====================
const blankKpiData = {
  countriesCount: 1,
  countryName: '-',
  taxRegistrationsCount: '-',
  compliancesCount: '-',
  processedRecordsCount: '-',
  expenseAmount: '-',
  expenseLiableToWht: '-',
  whtAmount: '-',
}

// ==================== Helpers ====================
const mapKpiResponseToStatData = (data = {}) => {
  let modifiedData = [
    { label: 'Tax registrations', value: data?.taxRegistrationsCount },
    { label: 'Compliances', value: data?.compliancesCount },
    { label: 'Processed records', value: data?.processedRecordsCount },
    { label: 'Expense amount', value: data?.expenseAmount },
    { label: 'Expense liable to WHT', value: data?.expenseLiableToWht },
    { label: 'WHT amount', value: data?.whtAmount },
  ]
  if (data?.countriesCount > 1) {
    modifiedData = [
      { label: 'Countries', value: data?.countriesCount },
      ...modifiedData,
    ]
  } else {
    modifiedData = [
      { label: 'Country', value: data?.countryName },
      ...modifiedData,
    ]
  }
  return modifiedData
}

const StatBoxGrid = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [statData, setStatData] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Effects ====================
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const kpiResponse = await getDashboardKpiCards(appliedFilters)
        processData(kpiResponse)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
  }, [optionsLoaded, appliedFilters])

  // ==================== Component Helpers ====================
  const processData = kpiResponse => {
    const kpiData = kpiResponse?.data?.data
    if (kpiResponse?.success && kpiData) {
      setStatData(mapKpiResponseToStatData(kpiData))
    } else {
      setStatData(mapKpiResponseToStatData(blankKpiData))
    }
  }

  // ==================== Return ====================
  if (!dataLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          minHeight: '86px',
          border: '1px solid var(--border-subtel)',
        }}
      >
        <Typography
          sx={{
            color: 'var(--forbrand-primary)',
            fontSize: 'var(--font-size-lg)',
          }}
        >
          Loading...
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        width: '100%',
      }}
    >
      {statData.map((item, index) => (
        <Box
          key={index}
          sx={{
            border: '1px solid var(--border-subtel)',
            px: 1,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '86px',
            flex: '1 1 0',
          }}
        >
          <Typography
            sx={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--forbrand-primary)',
              fontWeight: 400,
              lineHeight: '140%',
              letterSpacing: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </Typography>
          <Typography
            sx={{
              fontSize: 'var(--font-size-lg)',
              color: 'var(--black-color)',
              fontWeight: 500,
              lineHeight: '130%',
              letterSpacing: '-1%',
              paddingTop: '4px',
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

export default StatBoxGrid
